
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RolePermission, PermissionLevel } from "./types";

export const useRolePermissions = (roleId: string | null) => {
  return useQuery({
    queryKey: ["role_permissions", roleId],
    queryFn: async () => {
      if (!roleId) return [];
      
      const { data, error } = await supabase
        .from("role_permissions")
        .select(`
          *,
          resource:resources(*)
        `)
        .eq("role_id", roleId)
        .order("resource(name)");

      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: !!roleId,
  });
};

export const useUpdatePermissions = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      roleId, 
      permissions 
    }: { 
      roleId: string, 
      permissions: { resource_id: string, permission: PermissionLevel }[] 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { role_id: roleId, permissions, action: "update_permissions" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success("Permissions updated successfully");
      queryClient.invalidateQueries({ queryKey: ["role_permissions", variables.roleId] });
    },
    onError: (error) => {
      toast.error(`Failed to update permissions: ${error.message}`);
    }
  });
};
